import { InboxOutlined } from "@ant-design/icons";
import { Alert, Col, Form, Input, Pagination, Row, Table, Typography } from "antd";
import { Content } from "antd/es/layout/layout";
import { TablePaginationConfig } from "antd/es/table";
import { ColumnTypeCustom, HangGiamThueModel, QueryModel } from "../../../model/searchHangGiamThueModels";
import { useMemo, useRef, useState } from "react";
import './SearchHangGiamThue.css'
import useAxios from "../../../helper/axios";
import { FilterValue, SortOrder, SorterResult } from "antd/es/table/interface";
import TaxReduceSuggestion from "./TaxReduceSuggestion";
const { Text } = Typography;
const PAGE_SIZE = 50;
const countParam = "@odata.count";
const getEmptyTextOverlay = () => {
    return (
        <>
            <InboxOutlined className="table-local-icon" /><br />
            <span>Không tìm thấy dữ liệu, vui lòng thử tìm kiếm lại</span>
        </>
    )
}
const getTableFooter = () => (
    <Row>
        <Col span={12} className="footer-ref">
            <p >Tài liệu tham khảo:&nbsp;
                <a rel="noreferrer" href='https://minvoice.vn/nghi-dinh-44-2023-nd-cp-giam-thue-gia-tri-gia-tang-gtgt-nam-2023/' target='_blank'>Nghị định 44/2023/NĐ-CP</a>&nbsp;|&nbsp;
                <a rel="noreferrer" href='https://docs.google.com/spreadsheets/d/1ephNvlgn_fcNuoX4KHvhmpCI8kIA9a333rcAv9CYP4c/edit#gid=0' target='_blank'>Danh sách HHDV không được giảm thuế theo Nghị định 44/2023/NĐ-CP</a>
            </p>
        </Col>
        <Col span={12} className="footer-copyright">
            <p>
                Copyrights © {new Date().getFullYear()} Công ty TNHH Hóa đơn điện tử <strong><span>
                    M-Invoice
                </span></strong>. All Rights Reserved.
            </p>
        </Col>
    </Row>)
const columnRender: (value: any, record: HangGiamThueModel, index: number, filter: string | null) => React.ReactNode | undefined
    = (value, record, index, filter) => {
        let text = value;
        if (!filter) return text;
        text = String(text).replace(filter, `<mark>${filter}</mark>`);
        return <span dangerouslySetInnerHTML={{ __html: text }} />
    }
const getFilterQueryString: (dataColumns: ColumnTypeCustom<HangGiamThueModel>[], rawFilter: string | null) => string | null
    = (dataColumns, rawFilter) => {
        if (!rawFilter) {
            return null;
        }
        return dataColumns
            .filter((val) => val.isComposeFilter)
            .map((val) => `contains(${val.dataIndex}, '${rawFilter}')`)
            .join(' or ')
    }
const getSortQueryString: (rawSort: Sorter | null) => string | null
    = (rawSort) => {
        if (!rawSort?.field) {
            return null;
        }
        let order = (rawSort?.order ?? "ascend") === "ascend" ? "asc" : "desc";
        return `${rawSort?.field} ${order}`;
    }
interface Sorter {
    field?: React.Key | undefined | readonly React.Key[];
    order?: SortOrder | undefined;
}
const searchHangGiamThueUrl = "odata/HangHoaGiamThueGTGT"
const SearchHangGiamThue: React.FC = () => {
    const axios = useAxios();
    let isInit = useRef(false);
    const [dataSource, setDataSource] = useState<HangGiamThueModel[]>([]);
    const [isAllTaxReduce, setIsAllTaxReduce] = useState<boolean | null>(null);
    const [filter, setFilter] = useState<string | null>(null);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: PAGE_SIZE
    })
    const [sort, setSort] = useState<Sorter | null>(null);
    const dataColumns: ColumnTypeCustom<HangGiamThueModel>[] = [
        {
            dataIndex: "maHang",
            key: "maHang",
            title: "Mã Hàng",
            align: "left",
            sorter: true,
            isComposeFilter: true,
            render: (value, record, index) => columnRender(value, record, index, filter),
        },
        {
            dataIndex: "tenHang",
            key: "tenHang",
            title: "Tên Hàng",
            align: "left",
            sorter: true,
            isComposeFilter: true,
            render: (value, record, index) => columnRender(value, record, index, filter),
        },
        {
            dataIndex: "noiDung",
            key: "noiDung",
            title: "Nội dung",
            align: "left",
            sorter: true,
            isComposeFilter: true,
            render: (value, record, index) => columnRender(value, record, index, filter),
        },
        {
            dataIndex: "maSoHoSo",
            key: "maSoHoSo",
            title: "Mã số hồ sơ",
            align: "left",
            sorter: true,
            isComposeFilter: true,
            render: (value, record, index) => columnRender(value, record, index, filter),
        },
        {
            dataIndex: "phuLuc",
            key: "phuLuc",
            title: "Phụ Lục",
            align: "left",
            sorter: true,
            isComposeFilter: false,
            render: (value, record, index) => columnRender(value, record, index, filter),
        },
        {
            dataIndex: "ketQua",
            key: "ketQua",
            title: "Kết quả",
            align: "center",
            sorter: true,
            render: (value, record, index) => {
                let isTrue = record.ketQua ?? false;
                return <Alert
                    style={{ padding: 0 }}
                    type={isTrue ? "success" : "warning"}
                    description={<Text type={isTrue ? "success" : "warning"}>{isTrue ? "Có giảm thuế" : "Không giảm thuế"}</Text>}
                />
            },
            isComposeFilter: false,
        }
    ]
    const getRows = () => {
        let queryModel: QueryModel = {
            $filter: getFilterQueryString(dataColumns, filter),
            $top: pagination?.pageSize ?? PAGE_SIZE,
            $skip: ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? PAGE_SIZE),
            $orderby: getSortQueryString(sort),
            $count: true
        }
        axios.get(searchHangGiamThueUrl, { params: queryModel })
            .then((response) => {
                if (!response) return;
                setDataSource(response.data.value);
                setIsAllTaxReduce(response.data[countParam] === 0 ? null : !response.data.value.some((val: HangGiamThueModel) => val.ketQua === false))
                setPagination({
                    ...pagination,
                    total: response.data[countParam]
                })
            })
    }
    useMemo(() => {
        if (!isInit.current) {
            isInit.current = true;
            return;
        }
        getRows();
    }, [filter, pagination.current, pagination.pageSize, sort?.field, sort?.order])

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filter: Record<string, FilterValue | null>,
        sorter: SorterResult<HangGiamThueModel> | SorterResult<HangGiamThueModel>[],
    ) => {
        let sorterTemp = sorter as SorterResult<HangGiamThueModel>;
        if (!sorterTemp.order) {
            setSort(null);
            return
        }
        setSort({ ...sorterTemp });
    };

    return (
        <Content className="search-hang-giam-thue-body">
            <Row className="search-box-row">
                <Col xs={24} sm={24} md={12}>
                    <Form.Item label="Tìm kiếm">
                        <Input.Search
                            type="search"
                            placeholder="Tìm kiếm theo mã hàng, tên mặt hàng, nội dung, mã số hồ sơ"
                            name="searchQuery"
                            onSearch={(value) => setFilter(value.trim())}
                        />

                    </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} className="search-pagination">
                    <Pagination {...pagination}
                        onChange={(page, pageSize) =>
                            setPagination({
                                ...pagination,
                                pageSize: pageSize,
                                current: page
                            })}
                    ></Pagination>
                </Col>
            </Row>
            <TaxReduceSuggestion isAllTaxReduce={isAllTaxReduce} />
            <Table className="search-hang-giam-thue-table"
                columns={dataColumns}
                locale={{ emptyText: getEmptyTextOverlay }}
                scroll={{
                    scrollToFirstRowOnChange: true,
                    y: "33vh"
                }}
                rowKey={(record) => record.id.toString()}
                dataSource={dataSource}
                pagination={false}
                tableLayout="fixed"
                footer={getTableFooter}
                onChange={handleTableChange}
            >
            </Table>

        </Content>
    )
}
export default SearchHangGiamThue;